// src/components/DriverPicture.js
import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Modal,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Close, Star, StarBorder, ArrowBack, ArrowForward } from '@mui/icons-material';
import PictureStatusChip from './PictureStatusChip';
import { format } from 'date-fns'; // For formatting timestamps
import { setPrimaryPicture } from '../api';

const DriverPicture = ({
  picture,
  imageUrl,
  onStatusChange,
  driverId,
  onPrimaryChange,
  showStatusBadge = true,
  pictureList = [],
  currentIndex = 0,
  imageUrls = {}
}) => {
  const [open, setOpen] = useState(false);
  const [currentPictureIndex, setCurrentPictureIndex] = useState(currentIndex);
  const [showCycleMessage, setShowCycleMessage] = useState(false);

  useEffect(() => {
    console.log('DriverPicture render for picture:', picture.picture_id, 'is_primary:', picture.is_primary);
  }, [picture.picture_id, picture.is_primary]);

  useEffect(() => {
    setCurrentPictureIndex(currentIndex);
  }, [currentIndex]);

  const handleOpen = () => {
    setOpen(true);
    setShowCycleMessage(false);
  };
  const handleClose = () => setOpen(false);

  const handlePictureClick = (event) => {
    // Only open modal if the click was directly on the image or its container
    if (event.target.tagName === 'IMG' || event.target.tagName === 'DIV') {
      handleOpen();
    }
  };

  const handleStatusChange = (pictureId, newStatus) => {
    if (onStatusChange) {
      onStatusChange(pictureId, newStatus);
    }
  };

  const handleSetPrimary = async () => {
    try {
      console.log('handleSetPrimary called for picture:', picture.picture_id);
      const token = sessionStorage.getItem('fleetToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('Making API call to set primary picture');
      const result = await setPrimaryPicture(token, picture.picture_id);
      console.log('API call result:', result);

      // Only update UI if API call was successful (200 response)
      if (onPrimaryChange) {
        console.log('Calling onPrimaryChange with pictureId:', picture.picture_id);
        onPrimaryChange(picture.picture_id);
      } else {
        console.log('onPrimaryChange callback not provided');
      }
    } catch (error) {
      console.error('Error setting primary picture:', error);
      // Don't update UI if API call failed
    }
  };

  const handleNextPicture = () => {
    if (pictureList.length <= 1) return;

    const nextIndex = (currentPictureIndex + 1) % pictureList.length;
    setCurrentPictureIndex(nextIndex);

    if (nextIndex === 0 && currentPictureIndex === pictureList.length - 1) {
      setShowCycleMessage(true);
      setTimeout(() => setShowCycleMessage(false), 3000); // Hide message after 3 seconds
    } else {
      setShowCycleMessage(false);
    }
  };

  const handlePrevPicture = () => {
    if (pictureList.length <= 1) return;

    const prevIndex = currentPictureIndex === 0 ? pictureList.length - 1 : currentPictureIndex - 1;
    setCurrentPictureIndex(prevIndex);
    setShowCycleMessage(false);
  };

  return (
    <>
      <Paper
        elevation={1}
        sx={{
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
          }}
        >
          <Box
            onClick={handlePictureClick}
            sx={{
              cursor: 'pointer',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={`Driver Picture ${picture.picture_uid}`}
              style={{ maxWidth: '100%', maxHeight: '100px', objectFit: 'cover' }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              Loading...
            </Box>
          )}
          </Box>
          
          {/* Primary/Favorite Icon - Only show for pictures with status < 40 */}
          {picture.picture_status < 40 && (
            onPrimaryChange ? (
              <Tooltip title={picture.is_primary ? "Primary picture" : "Set as primary picture"}>
                <IconButton
                  onClick={handleSetPrimary}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    },
                    padding: '4px',
                  }}
                >
                  {picture.is_primary ? (
                    <Star sx={{ color: '#ff9800', fontSize: '20px' }} />
                  ) : (
                    <StarBorder sx={{ color: '#666', fontSize: '20px' }} />
                  )}
                </IconButton>
              </Tooltip>
            ) : (
              // Read-only star display when onPrimaryChange is undefined
              <Box
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  borderRadius: '4px',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Star sx={{ color: '#ff9800', fontSize: '20px' }} />
              </Box>
            )
          )}
        </Box>
        {showStatusBadge && (
          <Box sx={{ mt: 1 }}>
            <PictureStatusChip
              status={picture.picture_status}
              pictureId={picture.picture_id}
              driverId={driverId}
              onStatusChange={handleStatusChange}
            />
          </Box>
        )}
        <Typography variant="caption" sx={{ mt: 1 }}>
          {format(new Date(picture.upload_date), 'yyyy-MM-dd HH:mm:ss')}
        </Typography>
      </Paper>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="picture-modal-title"
        aria-describedby="picture-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            maxWidth: '90%',
            maxHeight: '90%',
            overflow: 'auto',
          }}
        >
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <Close />
          </IconButton>
          <Typography id="picture-modal-title" variant="h6" gutterBottom>
            Driver Picture {pictureList.length > 1 ? `(${currentPictureIndex + 1} of ${pictureList.length})` : ''}
          </Typography>

          {/* Navigation buttons */}
          {pictureList.length > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <IconButton
                onClick={handlePrevPicture}
                sx={{ color: 'primary.main' }}
                aria-label="Previous picture"
              >
                <ArrowBack />
              </IconButton>

              {showCycleMessage && (
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{
                    fontStyle: 'italic',
                    textAlign: 'center',
                    flex: 1,
                    mx: 2
                  }}
                >
                  Starting from first picture again
                </Typography>
              )}

              <IconButton
                onClick={handleNextPicture}
                sx={{ color: 'primary.main' }}
                aria-label="Next picture"
              >
                <ArrowForward />
              </IconButton>
            </Box>
          )}

          {(() => {
            const currentPicture = pictureList[currentPictureIndex] || picture;
            const currentImageUrl = imageUrls[currentPicture.picture_uid];

            return (
              <>
                {currentImageUrl ? (
                  <img
                    src={currentImageUrl}
                    alt={`Driver Picture ${currentPicture.picture_uid}`}
                    style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '70vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    Loading...
                  </Box>
                )}

                {showStatusBadge && (
                  <Box sx={{ mt: 2 }}>
                    <PictureStatusChip
                      status={currentPicture.picture_status}
                      pictureId={currentPicture.picture_id}
                      driverId={driverId}
                      onStatusChange={handleStatusChange}
                    />
                  </Box>
                )}

                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Uploaded: {format(new Date(currentPicture.upload_date), 'yyyy-MM-dd HH:mm:ss')}
                </Typography>
              </>
            );
          })()}
        </Box>
      </Modal>
    </>
  );
};

export default DriverPicture;
